import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import DashboardLayout from './DashboardLayout';
import styles from './COODashboard.module.css';
import { 
  FiClock, FiTruck, FiUsers, FiCalendar,
  FiClipboard, FiCheckCircle, FiAlertCircle,
  FiBarChart2, FiTrendingUp, FiArrowUp, FiArrowDown,
  FiPackage, FiSettings, FiActivity
} from 'react-icons/fi';

const COODashboard = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [operationalMetrics, setOperationalMetrics] = useState([]);
  const [projectStatus, setProjectStatus] = useState([]);
  const [resourceUtilization, setResourceUtilization] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);

  useEffect(() => {
    fetchUser();
    fetchDashboardData();
  }, []);

  const fetchUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
      setUser(profile);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      setOperationalMetrics([
        { metric: 'Project Delivery Rate', value: '94%', target: '95%', status: 'good' },
        { metric: 'Resource Utilization', value: '82%', target: '85%', status: 'warning' },
        { metric: 'Process Efficiency', value: '76%', target: '80%', status: 'warning' },
        { metric: 'Quality Index', value: '96%', target: '95%', status: 'good' },
        { metric: 'On-Time Delivery', value: '88%', target: '90%', status: 'warning' }
      ]);

      setProjectStatus([
        { name: 'E-Commerce Platform', progress: 85, health: 'good', deadline: 'Nov 15, 2024', team: 'Engineering' },
        { name: 'Mobile App Launch', progress: 62, health: 'warning', deadline: 'Dec 1, 2024', team: 'Mobile Dev' },
        { name: 'Cloud Migration', progress: 45, health: 'critical', deadline: 'Jan 15, 2025', team: 'DevOps' },
        { name: 'ERP Implementation', progress: 78, health: 'good', deadline: 'Dec 10, 2024', team: 'Enterprise' },
        { name: 'Security Audit', progress: 92, health: 'good', deadline: 'Oct 30, 2024', team: 'Security' }
      ]);

      setResourceUtilization([
        { team: 'Engineering', utilized: 88, available: 12, total: 45 },
        { team: 'Design', utilized: 75, available: 25, total: 20 },
        { team: 'QA', utilized: 92, available: 8, total: 15 },
        { team: 'Product', utilized: 70, available: 30, total: 12 },
        { team: 'DevOps', utilized: 85, available: 15, total: 10 }
      ]);

      setPendingApprovals([
        { id: 1, title: 'Budget Increase Request', requester: 'Engineering Head', amount: '₹50L', priority: 'high' },
        { id: 2, title: 'New Vendor Onboarding', requester: 'Procurement', vendor: 'Cloud Services', priority: 'medium' },
        { id: 3, title: 'Infrastructure Upgrade', requester: 'IT Manager', cost: '₹25L', priority: 'critical' }
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="COO">
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="COO">
      <div className={styles.cooDashboard}>
        <div className={styles.header}>
          <h1>COO Dashboard</h1>
          <p>Operational Excellence & Project Delivery</p>
        </div>

        {/* Operational KPIs */}
        <div className={styles.kpiGrid}>
          {operationalMetrics.map((metric, index) => (
            <div key={index} className={styles.kpiCard}>
              <h3>{metric.metric}</h3>
              <div className={styles.kpiValue}>{metric.value}</div>
              <div className={styles.kpiTarget}>Target: {metric.target}</div>
              <div className={`${styles.kpiStatus} ${styles[metric.status]}`}>
                {metric.status === 'good' ? <FiCheckCircle /> : <FiAlertCircle />}
                {metric.status === 'good' ? 'On Track' : 'Needs Attention'}
              </div>
            </div>
          ))}
        </div>

        {/* Project Portfolio */}
        <div className={styles.sectionCard}>
          <h2><FiPackage /> Active Projects Portfolio</h2>
          <div className={styles.projectsList}>
            {projectStatus.map((project, index) => (
              <div key={index} className={styles.projectCard}>
                <div className={styles.projectHeader}>
                  <div>
                    <h3>{project.name}</h3>
                    <span className={styles.projectTeam}>{project.team}</span>
                  </div>
                  <div className={`${styles.healthBadge} ${styles[project.health]}`}>
                    {project.health}
                  </div>
                </div>
                <div className={styles.projectProgress}>
                  <div className={styles.progressInfo}>
                    <span>Progress</span>
                    <span>{project.progress}%</span>
                  </div>
                  <div className={styles.progressBar}>
                    <div className={styles.progress} style={{ width: `${project.progress}%` }}></div>
                  </div>
                </div>
                <div className={styles.projectDeadline}>
                  <FiCalendar /> Deadline: {project.deadline}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Resource Allocation */}
        <div className={styles.twoColumnGrid}>
          <div className={styles.sectionCard}>
            <h2><FiUsers /> Resource Utilization</h2>
            <div className={styles.resourceList}>
              {resourceUtilization.map((resource, index) => (
                <div key={index} className={styles.resourceItem}>
                  <div className={styles.resourceHeader}>
                    <span className={styles.teamName}>{resource.team}</span>
                    <span className={styles.teamCount}>{resource.total} members</span>
                  </div>
                  <div className={styles.utilizationBar}>
                    <div className={styles.utilized} style={{ width: `${resource.utilized}%` }}></div>
                    <div className={styles.available} style={{ width: `${resource.available}%` }}></div>
                  </div>
                  <div className={styles.utilizationStats}>
                    <span>Utilized: {resource.utilized}%</span>
                    <span>Available: {resource.available}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.sectionCard}>
            <h2><FiClipboard /> Pending Approvals</h2>
            <div className={styles.approvalsList}>
              {pendingApprovals.map((approval) => (
                <div key={approval.id} className={styles.approvalItem}>
                  <div className={`${styles.approvalPriority} ${styles[approval.priority]}`}></div>
                  <div className={styles.approvalInfo}>
                    <h4>{approval.title}</h4>
                    <p>From: {approval.requester}</p>
                    {approval.amount && <span>Amount: {approval.amount}</span>}
                    {approval.vendor && <span>Vendor: {approval.vendor}</span>}
                  </div>
                  <div className={styles.approvalActions}>
                    <button className={styles.approveBtn}>Approve</button>
                    <button className={styles.rejectBtn}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default COODashboard;